import { useState } from "react";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";

import type { EprUserLink } from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import { ConfirmationModal } from "../ConfirmationModal.js";
import {
  Banner,
  BannerList,
  Button,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";

function DeleteLinkButton({
  link,
  onDeleted,
}: {
  link: EprUserLink;
  onDeleted: () => void;
}) {
  const [confirmFn, setConfirmFn] = useState<{
    confirmFn: () => void;
    message: string;
  } | null>(null);

  const deleteLink = async () => {
    try {
      const result = await api.eprClickThrough.links.delete(link.id);
      if (result.status === 204) {
        renderSuccessToast({
          message: `Removed remembered sign-in for ${link.eprUsername}`,
        });
        onDeleted();
      } else {
        renderErrorToast({
          message: `Failed to remove link: ${result.data?.reason ?? "Unknown error"}`,
        });
      }
    } catch {
      renderErrorToast({ message: "Failed to remove link" });
    }
    setConfirmFn(null);
  };

  return (
    <>
      {confirmFn && (
        <ConfirmationModal
          message={confirmFn.message}
          confirmFn={confirmFn.confirmFn}
          closeFn={() => {
            setConfirmFn(null);
          }}
        />
      )}
      <Button
        label={"Clear"}
        color={"error"}
        testId={`clear-link-${link.id}`}
        onClick={() => {
          setConfirmFn({
            message: `Clear the remembered sign-in linking "${link.eprUsername}" (${link.externalSystemType}) to ${link.userEmail}? They will be asked to sign in normally next time.`,
            confirmFn: () => {
              void deleteLink();
            },
          });
        }}
      />
    </>
  );
}

export function EprUserLinksManagementPage() {
  const [filterQuery, setFilterQuery] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const [{ data, loading, error }, refetch] =
    apiHooks.eprClickThrough.searchLinks(
      filterQuery,
      pageSize,
      page * pageSize,
    );

  const numPages = data?.count ? Math.ceil(data.count / pageSize) : 1;

  return (
    <Box>
      <Box sx={{ display: "flex", width: "100%", marginTop: "1em" }}>
        <TextField
          placeholder={"Filter by user, EPR username or system…"}
          sx={{ width: "100%" }}
          value={filterQuery}
          onChange={(e) => {
            setFilterQuery(e.target.value);
            setPage(0);
          }}
        />
      </Box>

      {!data && loading && (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Banner bannerType={BannerList.ERROR} title={error.message} />
      )}

      {!loading &&
        data &&
        (data.results.length > 0 ? (
          <Box>
            <Box
              sx={{ width: "100%", display: "flex", justifyContent: "center" }}
            >
              <button
                aria-label="Previous page"
                disabled={page === 0}
                onClick={() => {
                  if (page > 0) {
                    setPage(page - 1);
                  }
                }}
              >
                &#60;
              </button>
              <Box sx={{ marginLeft: "1em", marginRight: "1em" }}>
                Page {page + 1} of {numPages}
              </Box>
              <button
                aria-label="Next page"
                disabled={page >= numPages - 1}
                onClick={() => {
                  if (page < numPages - 1) {
                    setPage(page + 1);
                  }
                }}
              >
                &#62;
              </button>
            </Box>
            <TableContainer>
              <Table size="small" aria-label="remembered sign-in links">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>EPR</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Instance</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>EPR username</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.results.map((link) => (
                    <TableRow key={link.id}>
                      <TableCell>{link.userName}</TableCell>
                      <TableCell>{link.userEmail}</TableCell>
                      <TableCell>{link.externalSystemType}</TableCell>
                      <TableCell>{link.instanceDisplayName}</TableCell>
                      <TableCell>{link.eprUsername}</TableCell>
                      <TableCell>
                        <DeleteLinkButton
                          link={link}
                          onDeleted={() => {
                            void refetch();
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <p>No remembered sign-in links found</p>
        ))}
    </Box>
  );
}
